/**
 * Aggregations over a CSR view that the state panel needs.
 *
 * SEPARATE FROM `csr.ts` ON PURPOSE. `csr.ts` is the projection: it builds the
 * views and carries the leak-audit vocabulary, the forbidden-key tables and the
 * whole canonical-to-CSR machinery. None of that belongs in the browser bundle,
 * and importing one small function from it dragged all of it in — about 6 KB of
 * build-time-only tables shipped to a CSR's browser. This module depends on the
 * CSR types and nothing else.
 */

import type { CsrTopicView } from "./csr";
import type { EntityType } from "./model";

/**
 * The products a CSR cannot sell in this jurisdiction, named by product.
 *
 * This is the single definition of what the state panel's "Cannot be sold here"
 * banner lists, exported rather than inlined in the component so the rule is
 * unit-testable without rendering and so there is exactly one place it can be
 * got wrong.
 *
 * TWO KINDS OF "UNAVAILABLE", and only one of them belongs here:
 *
 *   an unavailable SERVICE      we do not sell this product here. A real gap a
 *                               CSR must not promise around. Listed.
 *   entity ROLLOUT METADATA     this entity type has not launched here yet.
 *                               A fact about the rollout of the whole entity,
 *                               not about any product sold alongside it, and
 *                               carried on the view as `productAvailability`
 *                               besides. NOT listed.
 *
 * Collapsing the two put "Cannot be sold here: Corporation formation" on every
 * pre-launch corporation record — beside "Close corporations", which is a
 * genuine product rule, as though they were the same kind of claim. They are
 * not, and in a tool built for reviewing pre-launch records the rollout line is
 * the one thing a reviewer already knows.
 *
 * There is no rollout row to exclude any more, and that is the point: the
 * projection stopped turning `EntityRecord.availability` into an item at all,
 * because rollout status is not a question about the state and a card of
 * formation requirements is not where it belongs. This function used to skip
 * such rows by a flag the projection set; the flag is gone with the row, which
 * is a stronger guarantee than classifying one — nothing here can list what is
 * never built. `productAvailability` on the view still carries the value.
 *
 * Named by the product rather than by the topic, because "Amendments" does not
 * tell a CSR that Article Amendment specifically cannot be sold.
 */
export function unavailableServiceLabels(topics: readonly CsrTopicView[]): string[] {
  const labels: string[] = [];
  for (const topic of topics) {
    for (const item of topic.items) {
      if (item.state !== "unavailable") continue;
      labels.push(item.label);
    }
  }
  return labels;
}

/**
 * Entity types whose panel shows the unavailable-services summary strip.
 *
 * LLC ONLY, and that is a product decision rather than an oversight.
 *
 * The strip exists for a CSR on a call: it surfaces, above the fold, the things
 * they must not promise before they have clicked into a topic. That is the right
 * design for the LLC side, which is the live sales surface.
 *
 * The corporation side is not that. While the migration is in flight the
 * corporation panel is a REVIEW surface — every one of its records is opened on
 * purpose, by someone checking it against the actual product. A standing
 * "Cannot be sold here" strip there summarises a fact the reviewer is already
 * holding, on every single record, above the content they came to read. It
 * earns its place on a sales panel and does not earn it on a review panel.
 *
 * Note what this does NOT do: it changes no status, deletes no fact, and hides
 * nothing. Close corporations are still unavailable, still say so in the Close
 * Corporation topic, and still carry that state in the data — which is why
 * `unavailableServiceLabels` above stays exactly as it was, and why the corp
 * bundle is byte-identical. Only the summary strip is entity-scoped.
 *
 * A list rather than a boolean so the question it answers is legible at the call
 * site, and so adding an entity type later is a data change here rather than a
 * new branch in the component.
 */
export const ENTITY_TYPES_WITH_UNAVAILABLE_BANNER: readonly EntityType[] = ["llc"];

/**
 * What the state panel's "Cannot be sold here" strip should list for this entity
 * type — empty where that entity type does not show the strip at all.
 *
 * The entity-type decision lives here, beside the rule it gates, rather than as
 * a condition in the component: the component asks what to render and is told,
 * and the reasoning sits next to the answer instead of in a JSX comment.
 */
export function unavailableServiceLabelsForPanel(
  entityType: EntityType,
  topics: readonly CsrTopicView[]
): string[] {
  if (!ENTITY_TYPES_WITH_UNAVAILABLE_BANNER.includes(entityType)) return [];
  return unavailableServiceLabels(topics);
}
